import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Link,
  Alert,
} from "@mui/material";
import { useDispatch } from "react-redux";
import AuthPageLayout from "../../components/layout/AuthPageLayout";
import { useRegisterMutation } from "../../features/auth/authApi";
import { setTokens } from "../../features/auth/authSlice";

export default function RegisterPage() {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const [register, { isLoading, error }] = useRegisterMutation();

  const [agencyName, setAgencyName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await register({
        agency_name: agencyName.trim(),
        owner_email: ownerEmail.trim(),
        owner_password: ownerPassword,
        owner_first_name: ownerFirstName.trim(),
        owner_last_name: ownerLastName.trim(),
      }).unwrap();
      dispatch(setTokens({ access: res.access, refresh: res.refresh }));
      nav("/app/dashboard");
    } catch {
      // error from mutation is shown below
    }
  };

  const apiError = error && "data" in error ? (error.data as { detail?: string })?.detail : null;

  return (
    <AuthPageLayout
      title="Create agency"
      subtitle="Set up your workspace."
      maxWidth={480}
    >
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {apiError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            label="Agency name"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            fullWidth
            required
            autoFocus
            autoComplete="organization"
          />

          <TextField
            label="Owner email"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            fullWidth
            required
            autoComplete="email"
          />

          <TextField
            label="Password"
            type="password"
            value={ownerPassword}
            onChange={(e) => setOwnerPassword(e.target.value)}
            fullWidth
            required
            autoComplete="new-password"
            helperText="At least 8 characters"
          />

          <TextField
            label="First name (optional)"
            value={ownerFirstName}
            onChange={(e) => setOwnerFirstName(e.target.value)}
            fullWidth
            autoComplete="given-name"
          />

          <TextField
            label="Last name (optional)"
            value={ownerLastName}
            onChange={(e) => setOwnerLastName(e.target.value)}
            fullWidth
            autoComplete="family-name"
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={!agencyName.trim() || !ownerEmail.trim() || ownerPassword.length < 8 || isLoading}
            sx={{ py: 1.5, mt: 0.5 }}
          >
            {isLoading ? "Creating…" : "Create agency"}
          </Button>

          <Button
            type="button"
            onClick={() => nav("/login")}
            variant="text"
            fullWidth
            sx={{ mt: -0.5 }}
          >
            Back to sign in
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/login" underline="hover" fontWeight={600}>
              Sign in
            </Link>
          </Typography>
        </Stack>
      </Box>
    </AuthPageLayout>
  );
}
